import * as React from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

interface DropdownMenuProps {
  children: React.ReactNode;
  trigger: React.ReactNode;
}

export const DropdownMenuContext = React.createContext<{ close: () => void }>({
  close: () => {},
});

export function DropdownMenu({ children, trigger }: DropdownMenuProps) {
  const [open, setOpen] = React.useState(false);
  const [position, setPosition] = React.useState({ top: 0, left: 0 });
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        triggerRef.current &&
        menuRef.current &&
        !triggerRef.current.contains(event.target as Node) &&
        !menuRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  React.useEffect(() => {
    if (open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const dropdownWidth = 224; // w-56 = 14rem = 224px
      const viewportWidth = window.innerWidth;
      
      // Calculate left position - center dropdown below the trigger button
      // Center the dropdown relative to the trigger button
      let left = rect.left + window.scrollX + (rect.width / 2) - (dropdownWidth / 2);
      
      // If dropdown would overflow on the left, align to left edge of trigger
      if (left < window.scrollX) {
        left = rect.left + window.scrollX;
      }
      
      // If dropdown would overflow on the right, adjust to fit
      if (left + dropdownWidth > window.scrollX + viewportWidth) {
        left = window.scrollX + viewportWidth - dropdownWidth - 8; // 8px padding from edge
      }
      
      setPosition({
        top: rect.bottom + window.scrollY + 4,
        left: Math.max(window.scrollX + 8, left), // Ensure at least 8px from left edge
      });
    }
  }, [open]);

  const close = () => setOpen(false);

  return (
    <DropdownMenuContext.Provider value={{ close }}>
      <div className="relative inline-block" ref={triggerRef}>
        <div 
          onClick={(e) => {
            e.stopPropagation();
            setOpen(!open);
          }} 
          className="cursor-pointer inline-flex items-center justify-center"
        >
          {trigger}
        </div>
      </div>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            className="fixed w-56 bg-popover rounded-md shadow-xl z-[100] border border-border py-1 min-w-[200px]"
            style={{
              top: `${position.top}px`,
              left: `${position.left}px`,
            }}
          >
            {children}
          </div>,
          document.body
        )}
    </DropdownMenuContext.Provider>
  );
}

interface DropdownMenuItemProps {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  onClose?: () => void;
}

export function DropdownMenuItem({
  children,
  onClick,
  disabled,
  className,
}: DropdownMenuItemProps) {
  const { close } = React.useContext(DropdownMenuContext);

  const handleClick = () => {
    if (!disabled && onClick) {
      onClick();
      close();
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={disabled}
      className={cn(
        'w-full text-left px-4 py-2 text-sm text-popover-foreground hover:bg-accent hover:text-accent-foreground flex items-center gap-2 transition-colors first:rounded-t-md last:rounded-b-md',
        disabled && 'opacity-50 cursor-not-allowed',
        !disabled && 'cursor-pointer',
        className
      )}
    >
      {children}
    </button>
  );
}

